import GraphqlClient from './GraphqlClients';
import gql from 'graphql-tag';

// Same limit Document Trace uses; a response with exactly this many rows may be truncated.
export const API_LIST_LIMIT = 200;
export const PAGE_SIZE = 10;

const ScraperDocumentsService = () => {
  const gqlClient = GraphqlClient();

  // `link` is intentionally not requested: it builds a presigned URL and is slow (~2.5s each).
  const documentsQuery = gql`
    query ScraperDocuments($filter: scp_AccountDocumentsFilter!, $limit: Int, $offset: Int) {
      documents: scp_documents(filter: $filter, limit: $limit, offset: $offset) {
        id
        documentStorageRef
        date
        type
        status
        requiresTransformation
        relatedDocumentId
        processStatus
        parsed
        processInApi
        documentType
        infoProvider
        credentialId
      }
    }
  `;

  const credentialsQuery = gql`
    query AccountCredentials($filter: scp_CredentialsFilter, $limit: Int) {
      credentials: scp_credentials(filter: $filter, limit: $limit) {
        id
        username
        infoProvider
      }
    }
  `;

  const parserDocumentsQuery = gql`
    query PipelineParserDocuments($filter: parser_DocumentsFilter) {
      documents: parser_documents(filter: $filter) {
        id
        scrapedDocumentId
        majorVersion
        minorVersion
        patchVersion
        split
      }
    }
  `;

  const parserFailsQuery = gql`
    query PipelineParserFails($filter: parser_ParserFailsFilter) {
      fails: parser_parserFails(filter: $filter) {
        id
        createdAt
        scrapedDocumentId
        exceptionClass
        debugMessage
      }
    }
  `;

  const paymentsQuery = gql`
    query PipelinePayments($filter: api_PaymentsFilter, $limit: Int) {
      payments: api_payments(filter: $filter, limit: $limit) {
        id
        scrapedDocumentExtId
        parsedDocumentExtId
        isBalanced
      }
    }
  `;

  const paymentFailsQuery = gql`
    query PipelinePaymentFails($filter: api_PaymentFailsFilter, $limit: Int) {
      fails: api_paymentFails(filter: $filter, limit: $limit) {
        id
        scrapedDocumentExtId
        parsedDocumentExtId
        debugMessage
        moreInfo
      }
    }
  `;

  const query = (options) =>
    gqlClient.query({ fetchPolicy: 'network-only', ...options }).then((data) => data.data);

  const getDocuments = (filter, offset = 0) =>
    query({
      query: documentsQuery,
      variables: { filter, limit: PAGE_SIZE, offset }
    }).then((data) => data?.documents || []);

  // The scraper filter matches `external_account_id` exactly (ILIKE without wildcards).
  const getCredentials = (filter) => {
    return query({
      query: credentialsQuery,
      variables: { filter, limit: 100 }
    })
      .then((data) => data?.credentials || [])
      .catch(() => []);
  };

  const getParserDocuments = (scrapedDocumentIds) =>
    query({ query: parserDocumentsQuery, variables: { filter: { scrapedDocumentIds } } }).then(
      (data) => data?.documents || []
    );

  const getParserFails = (scrapedDocumentIds) =>
    query({ query: parserFailsQuery, variables: { filter: { scrapedDocumentIds } } }).then(
      (data) => data?.fails || []
    );

  const withTruncation = (items) => ({ items, truncated: items.length >= API_LIST_LIMIT });

  const getPayments = (scrapedDocumentExtIds) =>
    query({
      query: paymentsQuery,
      variables: { filter: { scrapedDocumentExtIds }, limit: API_LIST_LIMIT }
    }).then((data) => withTruncation(data?.payments || []));

  const getPaymentFails = (scrapedDocumentExtIds) =>
    query({
      query: paymentFailsQuery,
      variables: { filter: { scrapedDocumentExtIds }, limit: API_LIST_LIMIT }
    }).then((data) => withTruncation(data?.fails || []));

  return {
    getDocuments,
    getCredentials,
    getParserDocuments,
    getParserFails,
    getPayments,
    getPaymentFails
  };
};

export default ScraperDocumentsService;
